import { FC, useEffect } from "react";
import { Controller } from "react-hook-form";
import { useI18n } from "../../hooks/useTranslations";
import { Priority } from "../../http/priority/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Input from "../ui/Input";
import Numeric from "../ui/Numeric";

export interface PriorityFormBody {
  name: string;
  days: number;
}

type PriorityFormProps = {
  priority?: Priority;
  onSubmit: (data: PriorityFormBody) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const PriorityForm: FC<PriorityFormProps> = ({ priority, onSubmit, isDisabled, isLoading }) => {
  const i18n = useI18n();

  const defaultValues = {
    name: priority?.name ?? "",
    days: priority?.days ?? 0,
  };

  return (
    <Form<PriorityFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ control, setValue, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [priority]);

        return (
          <>
            <Controller
              name="name"
              control={control}
              rules={{ required: i18n.t.common.fieldCannotBeEmpty }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.name} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} {...field} isInvalid={isInvalid} disabled={isDisabled || isLoading} />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="days"
              control={control}
              rules={{
                required: true,
                min: {
                  value: 1,
                  message: i18n.t.common.fieldCannotBeEmpty,
                },
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.days} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      min={1}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading}
                      onBlur={(value) => setValue("days", value ?? 0)}
                    />
                  )}
                </FormField>
              )}
            />
          </>
        );
      }}
    </Form>
  );
};

export default PriorityForm;
